import * as React from "react"
import { type Category, type DocEntry, type Lane } from "../lib/docs"
import { childrenOf, laneNodes, ownDir, type Node } from "../lib/tree"
import { CardGrid } from "./Cards"

/**
 * The project's page: every section, and what is directly inside each one.
 *
 * **The top level is fixed and nothing is created here.** These sections are what the
 * repository declares in `.agent-lanes`; they are not things somebody adds by clicking. A
 * `+` at this level would offer to make a seventh place for documents to go, which is the
 * one decision this page must not make casually — a lane with no question of its own is
 * where documents go missing.
 *
 * **Each section shows its own first level rather than a count.** A page of six cards
 * saying "10 items" answers nothing: the reader still has to open all six to find out
 * where anything is. Showing one level down turns the landing page from a menu into a map,
 * and it costs no request — the whole index arrived in one frame.
 */
export const Home: React.FC<{
  title: string
  repo: string
  categories: Category[]
  lanes: Lane[]
  entries: DocEntry[]
  lang: string
  declared: Set<string>
  laneSummary: (id: string) => string
  onOpen: (path: string) => void
  onEnter: (dir: string) => void
  onCategory: (id: string) => void
}> = ({ title, repo, categories, lanes, entries, lang, declared, laneSummary, onOpen,
        onEnter, onCategory }) => {
  const sections = [
    ...categories.map(c => ({
      id: c.id, label: c.label, note: laneSummary(c.id), lane: true,
      nodes: laneNodes(entries, c.id, declared),
    })),
    // A hidden lane keeps its documents and loses its menu row — not its reachability.
    ...lanes.filter(l => l.hidden)
      .map(l => ({ id: l.id, label: l.label, lane: true,
                   note: "Kept out of the menu, not out of reach.",
                   nodes: laneNodes(entries, l.id, declared) }))
      .filter(s => s.nodes.length),
  ]
  // Things filed above the lanes: a decision registry indexes every one of them, so it
  // belongs to none, and putting it inside one would claim it was that lane's.
  const above = childrenOf(entries, "docs").filter(n => !n.dir && !isLanePage(entries, n))

  return (
    <>
      <header className="sf-doc-head">
        <p className="sf-kicker">{repo.split("/").pop()}</p>
        <h1>{title}</h1>
        <p className="sf-lede">
          Everything here is read from your own repository, on your own machine.
        </p>
      </header>

      {!!above.length && (
        <section className="sf-home-section">
          <h2 className="sf-home-head"><span>Above the sections</span></h2>
          <CardGrid nodes={above} lang={lang} onOpen={onOpen} onEnter={onEnter} />
        </section>
      )}

      {sections.map(s => (
        <section key={s.id} className="sf-home-section">
          {/* No count here. The menu already carries one — for every document in the lane —
              and a second number beside it, counting cards instead, reads as a contradiction
              rather than as two measurements. The cards are on the page; they count
              themselves. */}
          <h2 className="sf-home-head">
            <button className="sf-home-link" onClick={() => onCategory(s.id)}>{s.label}</button>
          </h2>
          <p className="sf-home-note">{s.note}</p>
          {s.nodes.length
            ? <CardGrid nodes={s.nodes} lang={lang} onOpen={onOpen} onEnter={onEnter} />
            : <p className="sf-quiet sf-home-empty">Nothing filed here yet.</p>}
        </section>
      ))}
    </>
  )
}

/** A lane's own `index.md` describes a section; it is not a thing filed above them. */
function isLanePage(entries: DocEntry[], n: Node): boolean {
  return entries.some(e => e.path === n.path && ownDir(e))
}
